import { useEffect, useMemo } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { LenderStatus } from "../../types/deals";
import { getStatusConfig, validateStatusTransition } from "../../utils/statusConfig";
import { format } from "date-fns";



const schema = z.object({
  status: z.custom<LenderStatus>(),
  note: z.string().optional(),
  reason: z.string().optional(),
  termSheetFile: z.any().optional(),
  // Sanction fields
  sanctionAmount: z.coerce.number().optional(),
  sanctionDate: z.date().optional(),
  sanctionValidTill: z.date().optional(),
  collateralDetails: z.string().optional(),
  sanctionLetterFile: z.any().optional(),
  // Disbursement & payouts
  payoutBasis: z.enum(["sanctioned", "disbursed", "manual"]).optional(),
  disbursedAmount: z.coerce.number().optional(),
  // amounts
  customerPayout: z.coerce.number().optional(),
  lenderPayout: z.coerce.number().optional(),
  partnerPayout: z.coerce.number().optional(),
  // percents
  customerPayoutPercent: z.coerce.number().optional(),
  lenderPayoutPercent: z.coerce.number().optional(),
  partnerPayoutPercent: z.coerce.number().optional(),
});

export type StatusFormValues = z.infer<typeof schema>;

interface LenderStatusDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (values: StatusFormValues) => void;
  isHybrid: boolean;
  sanctionedAmountFromHistory?: number;
  lenderInstitution?: string;
  lenderName?: string;
  currentStatus?: LenderStatus;
  isLeasing?: boolean; // For leasing, remove Disbursed option
  LenderStatus?: any;
}

export function LenderStatusDialog({
  open,
  onOpenChange,
  onSubmit,
  sanctionedAmountFromHistory,
  lenderInstitution,
  lenderName,
  currentStatus,
  isLeasing = false,
  LenderStatus,

}: LenderStatusDialogProps) {
  const form = useForm<StatusFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { status: "" as LenderStatus, payoutBasis: undefined },
  });

  const availableStatuses = useMemo(() => {
    if (!isLeasing && sanctionedAmountFromHistory) {
      return LenderStatus.filter((s: any) => String(s.id) === "9");
    } else if (isLeasing) {
      return LenderStatus.filter((s: any) => String(s.id) !== "9");
    }
    return LenderStatus;
  }, [isLeasing, sanctionedAmountFromHistory]);

  useEffect(() => {
    if (open) {
      const existsInAvailable = availableStatuses.some(
        (s: any) => String(s.id) === String(currentStatus)
      );
  
      const newValue = existsInAvailable ? String(currentStatus) : "";
      const currentValue = form.getValues("status");
  
      if (currentValue !== newValue) {
        form.reset({
          ...form.getValues(),
          status: newValue as LenderStatus,
        });
      }
    }
  }, [open, currentStatus, availableStatuses]);

  const watchStatus = form.watch("status");
  const watchBasis = form.watch("payoutBasis");
  const disbursed = form.watch("disbursedAmount") ?? 0;
  const baseAmount = watchBasis === "sanctioned" ? (sanctionedAmountFromHistory ?? 0) : watchBasis === "disbursed" ? disbursed : 0;
  const customerPct = form.watch("customerPayoutPercent");
  const lenderPct = form.watch("lenderPayoutPercent");
  const partnerPct = form.watch("partnerPayoutPercent");

  const requiresReason = String(watchStatus) === "7";
  const needsSanction = String(watchStatus) === "8";
  const needsDisbursed = String(watchStatus) === "9";

  // When payout basis is selected (non-manual), compute payout ₹ amounts and store them in form values
  useEffect(() => {
    if (watchBasis !== "sanctioned" && watchBasis !== "disbursed") {
      // do not overwrite manual values; clear computed values when no basis selected
      form.setValue("customerPayout", undefined);
      form.setValue("lenderPayout", undefined);
      form.setValue("partnerPayout", undefined);
      return;
    }
    if (!baseAmount || baseAmount <= 0) return;

    const cp = ((Number(customerPct ?? 0) / 100) * baseAmount) || 0;
    const lp = ((Number(lenderPct ?? 0) / 100) * baseAmount) || 0;
    const pp = ((Number(partnerPct ?? 0) / 100) * baseAmount) || 0;

    form.setValue("customerPayout", cp, { shouldDirty: true });
    form.setValue("lenderPayout", lp, { shouldDirty: true });
    form.setValue("partnerPayout", pp, { shouldDirty: true });
  }, [watchBasis, baseAmount, customerPct, lenderPct, partnerPct, form]);



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {/* Lender Logo */}
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shrink-0">
              <span className="text-sm font-bold text-primary">
                {lenderInstitution?.charAt(0)?.toUpperCase() || "L"}
              </span>
            </div>
            <div>
              <div className="text-base">Update Status</div>
              <div className="text-sm font-normal text-muted-foreground">
                {lenderInstitution ?? ""}{lenderInstitution && lenderName ? " • " : ""}{lenderName ?? ""}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((vals) => onSubmit(vals))} className="space-y-5">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value ? String(field.value) : ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Status" />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {availableStatuses.map((s: any) => {
                        const config = getStatusConfig(s.id);
                        const StatusIcon = config?.icon;
                        const validation = validateStatusTransition(currentStatus, s.id);

                        return (
                          <SelectItem
                            key={s.id}
                            value={String(s.id)}
                            disabled={!validation.valid}
                          >
                            <div className="flex items-center gap-2">
                              {StatusIcon && (
                                <StatusIcon
                                  className={`h-4 w-4 ${config?.color || "text-gray-600"}`}
                                />
                              )}
                              {s.name}

                              {!validation.valid && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({validation.message})
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {requiresReason && (
              <FormField
                control={form.control}
                name="reason"
                rules={{ required: true }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason</FormLabel>
                    <FormControl>
                      <Input placeholder="Required" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {needsSanction && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium">Sanction Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="sanctionAmount"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sanction Amount *</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="Amount" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sanctionDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sanction Date</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            value={field.value ? format(field.value, "yyyy-MM-dd") : ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value ? new Date(value) : undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="sanctionValidTill"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sanction Valid Till</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          value={field.value ? format(field.value, "yyyy-MM-dd") : ""}
                          min={format(new Date(), "yyyy-MM-dd")}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value ? new Date(value) : undefined);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="collateralDetails"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Collateral/Security Details</FormLabel>
                      <FormControl>
                        <Textarea rows={2} placeholder="Enter collateral or security details..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sanctionLetterFile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Upload Sanction Letter</FormLabel>
                      <FormControl>
                        <Input type="file" onChange={(e) => field.onChange(e.target.files?.[0])} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {needsDisbursed && !isLeasing && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium">Disbursement & Payout Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="payoutBasis"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payout Basis</FormLabel>
                        <Select
                          onValueChange={(v) => field.onChange(v)}
                          value={(field.value as any) ?? ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select basis" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="sanctioned">Sanctioned Amount</SelectItem>
                            <SelectItem value="disbursed">Disbursed Amount</SelectItem>
                            <SelectItem value="manual">Enter Manually</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="disbursedAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Disbursed Amount</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {typeof sanctionedAmountFromHistory === 'number' && (
                  <div className="text-xs text-muted-foreground">Sanction amount (latest): ₹{new Intl.NumberFormat("en-IN").format(sanctionedAmountFromHistory)}</div>
                )}

                {watchBasis === 'manual' ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="customerPayout"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer Payout (₹)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lenderPayout"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lender Payout (₹)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="partnerPayout"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Partner Payout (₹)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="customerPayoutPercent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Customer Payout (%)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" {...field} />
                          </FormControl>
                          {(watchBasis === "sanctioned" || watchBasis === "disbursed") && (
                            <div className="text-xs text-muted-foreground">
                              = ₹{((Number(field.value ?? 0) / 100) * baseAmount || 0).toFixed(0)}
                            </div>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lenderPayoutPercent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lender Payout (%)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" {...field} />
                          </FormControl>
                          {(watchBasis === "sanctioned" || watchBasis === "disbursed") && (
                            <div className="text-xs text-muted-foreground">
                              = ₹{((Number(field.value ?? 0) / 100) * baseAmount || 0).toFixed(0)}
                            </div>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="partnerPayoutPercent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Partner Payout (%)</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.01" {...field} />
                          </FormControl>
                          {(watchBasis === "sanctioned" || watchBasis === "disbursed") && (
                            <div className="text-xs text-muted-foreground">
                              = ₹{((Number(field.value ?? 0) / 100) * baseAmount || 0).toFixed(0)}
                            </div>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
